
import { IIconProps, Panel, PanelType, PrimaryButton } from "@fluentui/react";
import React from "react";
import { useState } from "react";
import styles from "./InsightSidePanel.less";
import { SectionHeader } from "../../common/SectionHeader";
import InsightDetailsTop10Table from "./InsightDetailsTop10Table";
import { useGetInsightBaseInfo, useGetInsightDetailForRecommendationType, useGetInsightRCA, useGetInsightReCommendation, useGetInsightUsageTrend } from "../../../hooks/useInsightPlatformQuery";
import { EmpytCostView } from "../../substrateV2/common/EmptyCostView";
import { useSelector } from "react-redux";
import { IAppState } from "../../../store";
import { ServiceTreeItem } from "../../../models/serviceTree";
import { parseScenarioTag } from "../../../utils/PcmV2Utils";
import { useGetAppIdNameMap } from "../../../hooks/useSearchMetadataQuery";
import LoadingState from "../../ResponseBoundary/LoadingState";
import { is } from "date-fns/locale";

const closeIconProps: IIconProps = { iconName: "Cancel" };

interface IRecommendationInsightSidePanel {
    isPanelOpen: boolean;
    onDismiss: () => void;
    insightId: string;
}

const panelWidthSetting: Record<string, string> = {
    ['Init']: '60%',
    ['OpenEdit']: '1000px',
};

export function getWorkLoadNames(id: string, category: string, serviceIdMap: Map<string, ServiceTreeItem>, appIdNameMap: Map<string, string>) {
    if (category == "Service") {
        return serviceIdMap.get(id)?.n || id;
    } else if (category == "GriffinApplication") {
        return appIdNameMap.get(id);
    } else if (category == "GriffinProcessor" || category == "ScenarioTag") {
        const [appId, workloadName] = parseScenarioTag(id);
        const appName = appIdNameMap.get(appId);
        return `${workloadName}${appName ? `(${appName})` : ""}`;
    } else {
        return id;
    }
}

const RecommendationInsightSidePanel: React.FC<IRecommendationInsightSidePanel> = (props) => {

    const { onDismiss } = props;

    const [panelWidth, setpanelWidth] = useState<string>(panelWidthSetting.Init);

    const handleClose = () => {
        setpanelWidth(panelWidthSetting.Init);
        setIsClosed(true);
        onDismiss();
    }

    const handleCancel = () => {
        handleClose();
        setIsClosed(true);
    };

    const navigateToEFT = () => {
        window.open("https://msjaws.com/#/EfficiencyTrackerV2/AllProjects/Baremetal", "_blank");
    };

    const serviceIdMap = useSelector<IAppState, Map<string, ServiceTreeItem>>(state => state.serviceTree.indexMap);
    const appIdNameMap = useGetAppIdNameMap();

    const onRenderFooterContent = (): JSX.Element => (
        <div>
            <PrimaryButton className={styles.panelButton} onClick={handleCancel}>Close</PrimaryButton>
        </div>
    )

    const [isClosed, setIsClosed] = useState(true);



const { data: insightBaseInfo, isLoading: baseInfoIsLoading } = useGetInsightBaseInfo(props.insightId);

const { data: insightRecommendation, isLoading: recommendationIsLoading } = useGetInsightReCommendation(props.insightId);

const {data: insightRecomDetail, isLoading: recomDetailIsLoading} = useGetInsightDetailForRecommendationType(props.insightId);

    return (
        <Panel
            type={PanelType.custom}
            className={styles.container}
            customWidth={panelWidth}
            headerText={"Recommendation Details: " + insightBaseInfo?.insightName}
            headerClassName={styles.title}
            isOpen={props.isPanelOpen}
            onDismiss={handleClose}
            isBlocking={false}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
            isLightDismiss
            layerProps={{eventBubblingEnabled: true}}
        >
            <div className={styles.panelContent} id={"InsightSidePanel_Content"}>
                <div className={styles.divBlank}>
                    { baseInfoIsLoading ? (<LoadingState />): (insightBaseInfo == undefined  ? (
                        <EmpytCostView />
                    ) : (
                            <div style={{marginTop:20, marginBottom:10}}>
                                <table>
                                    <thead>
                                        <tr>
                                            <td style={{width:200}}><b style={{fontWeight:600}}>Status</b></td>
                                        </tr>
                                        <tr>
                                            <td style={{width:200}}>{insightBaseInfo.status}</td>                    
                                        </tr>
                                    </thead>
                                </table>
                                <table style={{paddingTop: 10, paddingBottom: 10}}>
                                    <thead>
                                        <tr>
                                            <td style={{width:200}}><b style={{fontWeight:600}}>Service Name</b></td>
                                            <td style={{width:200}}><b style={{fontSize:14, fontWeight:600}}>Impacted Resource</b></td>
                                        </tr>
                                        <tr>
                                            <td style={{width:200}}><span style={{fontSize:13}}>{serviceIdMap.get(insightBaseInfo.serviceId)?.n || insightBaseInfo.serviceId}</span></td>
                                            <td style={{width:200}}><span style={{fontSize:14}}>{insightBaseInfo.impactedResource}</span></td>
                                        </tr>
                                    </thead>
                                </table>
                                {Object.keys(insightBaseInfo.baseInfo).map((key, index) => (
                                    <table key={index} style={{paddingTop: 10, paddingBottom: 10}}>
                                        <thead>
                                            <tr>
                                                <td><b style={{fontWeight:600}}>{key}</b></td>
                                            </tr>
                                            <tr>
                                                <td><span style={{fontSize:13, wordBreak:"break-all"}}>{Object.values(insightBaseInfo!.baseInfo)[index]}</span></td>
                                            </tr>
                                        </thead>
                                    </table>
                                ))}
                            </div> 
                        ))}

                    <SectionHeader title="Details"/>

                    { recomDetailIsLoading ? (<LoadingState />) : (insightRecomDetail == undefined  ? (
                        <></>
                    ) : (
                        <InsightDetailsTop10Table data={insightRecomDetail}></InsightDetailsTop10Table>
                    ))}

                    <SectionHeader title="Take Action"/>
                    { recommendationIsLoading ? (<LoadingState />): (insightRecommendation == undefined ? (
                        <></>
                    ) : (
                        <div style={{ wordBreak: "break-word" }}>
                        {insightRecommendation.recommendationText
                          .split('\n')
                          .map((line, index) => (
                            <p key={index} style={{ marginBottom: "8px" }}>
                              {line}
                            </p>
                          ))}
                        <a
                          href={insightRecommendation.recommendationURL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {insightRecommendation.recommendationURL}
                        </a>
                      </div>
                    ))}

                    {/* Hide this for now */}
                    {/* <SectionHeader title="Track your savings"/>
                    <div style={{wordBreak:"break-word", marginTop:20, marginBottom:20}}>
                        <p style={{fontWeight:600}}>Create a project in the Efficiency Tracker to view your progress and savings for this work.</p>
                        <PrimaryButton className={styles.panelButton} onClick={navigateToEFT}>Track my savings</PrimaryButton>
                    </div> */}
                </div>                      
            </div>

            <div className={styles.panelContent}>
                <div className={styles.divBlank}></div>
            </div>
        </Panel>
    );
};

export default RecommendationInsightSidePanel;